// src/pages/Home.tsx
// @ts-nocheck
import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase.js';
import RecipeCardCompact from '../components/Shared/RecipeCards/RecipeCardCompact.tsx';
import fetchWithAuth from '../utils/fetchWithAuth.ts';
import BrandSpinner from '../components/ui/atoms/BrandSpinner/BrandSpinner.tsx';
import { currentSeason, capitalize } from '../utils/seasonal.ts';
import AddToMealPlanSheet from '../components/MealPlanner/AddToMealPlanSheet.tsx';
import { getCurrentWeekStart } from '../utils/getCurrentWeekStart.ts';

const Home = () => {
  const [recipes, setRecipes] = useState([]);
  const [followed, setFollowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popular, setPopular] = useState([]);
  const [seasonal, setSeasonal] = useState([]);
  const [following, setFollowing] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRecipe, setPickerRecipe] = useState(null);

  // Compute once per render:
  const season = useMemo(() => currentSeason(), []);
  const seasonalTitle = `${capitalize(season)} Picks`;
  const uid = auth.currentUser?.uid;

  // Fetch all recipes + who you follow (for initial “followed” list)
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchWithAuth(`/api/recipes`);
        setRecipes(data);

        if (uid) {
          const fSnap = await getDocs(collection(db, 'follows'));
          const followingIds = fSnap.docs
            .filter((f) => f.data().followerId === uid)
            .map((f) => f.data().followingId);
          // assume data is array of recipes, each has userId
          setFollowed(data.filter((r) => followingIds.includes(r.userId)));
        }
      } catch (err) {
        console.error('Error fetching recipes:', err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  // Fetch popular, seasonal, following for the home sections
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [popData, seaData, folData] = await Promise.all([
          fetchWithAuth(`/api/recipes/popular`),
          fetchWithAuth(`/api/recipes/seasonal`),
          uid ? fetchWithAuth(`/api/recipes/followed`) : Promise.resolve([]),
        ]);
        setPopular(popData);
        setSeasonal(seaData);
        setFollowing(folData);
      } catch (err) {
        console.error('Error fetching home sections:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  // Open the bottom‐sheet picker
  const openSheet = (r) => {
    setPickerRecipe(r);
    setPickerOpen(true);
  };

  // ─── bookmarks: helper to flip isBookmarked in a single array ───────────
  const flipBookmarkInArray = (arr, recipeId) => {
    return arr.map((r) =>
      r.id === recipeId ? { ...r, isBookmarked: !r.isBookmarked } : r
    );
  };

  // ─── main toggleBookmark handler ─────────────────────────────────────────
  const handleToggleBookmark = async (recipe) => {
    try {
      if (recipe.isBookmarked) {
        await fetchWithAuth(`/api/bookmarks`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recipeId: recipe.id }),
        });
      } else {
        await fetchWithAuth(`/api/bookmarks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recipeId: recipe.id }),
        });
      }

      // Update whichever section array(s) contain this recipe
      setPopular((prev) => flipBookmarkInArray(prev, recipe.id));
      setSeasonal((prev) => flipBookmarkInArray(prev, recipe.id));
      setFollowing((prev) => flipBookmarkInArray(prev, recipe.id));
      setRecipes((prev) => flipBookmarkInArray(prev, recipe.id));
      setFollowed((prev) => flipBookmarkInArray(prev, recipe.id));
    } catch (err) {
      console.error('Failed to toggle bookmark on Home:', err);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-10">
        <BrandSpinner />
      </div>
    );

  const sections = [
    { title: 'Popular Recipes', data: popular },
    { title: seasonalTitle, data: seasonal },
    { title: 'From People You Follow', data: following },
  ];

  const visibleSections = sections.filter((s) => s.data.length > 0);
  return (
    <div className="p-4 pb-20 bg-white space-y-2">
      {visibleSections.map((s, i) => (
        <div key={i} className="mb-1">
          <h2 className="text-xl font-semibold mb-3">{s.title}</h2>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            {s.data.map((r) => (
              <RecipeCardCompact
                key={r.id}
                recipe={r}
                isBookmarked={r.isBookmarked}
                onToggleBookmark={() => handleToggleBookmark(r)}
                onAddToPlan={() => openSheet(r)}
              />
            ))}
          </div>
        </div>
      ))}

      {pickerRecipe && (
        <AddToMealPlanSheet
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          recipe={{
            id: pickerRecipe.id,
            title: pickerRecipe.title,
            imageUrl: pickerRecipe.imageUrl,
          }}
          weekStart={getCurrentWeekStart()}
        />
      )}
    </div>
  );
};

export default Home;
